import { Moment } from 'moment';
import { useCallback, useMemo, useState } from 'react';
import { ResourceType } from 'api/resource-management/types';
import { useShowSourceTasks } from 'components/organisms/resource-management/scheduler-config/listeners';
import { SchedulerReference } from 'components/organisms/resource-management/types';
import { asAllocationPayload } from 'components/organisms/resource-management/utils/events-conversion/common';
import {
  ProjectTemplateTask,
  TemplateAllocation,
  useGetAllTasks,
} from 'data/templates/projects';
import { asTemplateAllocation } from '../utils/as-template-allocation';

type DurationParam = {
  startDay: number;
  duration: number;
};

const filterTasksByUserId = (tasks: ProjectTemplateTask[], userId: number) =>
  tasks.filter(({ assignee }) =>
    (assignee?.users ?? []).find(
      ({ userId: taskUserId }) => taskUserId === userId,
    ),
  );
const filterTasksByTeamId = (tasks: ProjectTemplateTask[], teamId: number) =>
  tasks.filter(({ assignee }) =>
    (assignee?.teams ?? []).find(
      ({ teamId: taskTeamId }) => taskTeamId === teamId,
    ),
  );

const filterTasksWithEffort = (tasks: ProjectTemplateTask[]) =>
  tasks.filter(({ effort }) => effort != null && effort > 0);
const isOverlappingTask = (
  { startDay: taskStartDay, duration: taskDuration }: ProjectTemplateTask,
  { startDay, duration }: DurationParam,
): boolean => {
  const taskEndDay = taskStartDay + taskDuration - 1;
  const durationEndDay = startDay + duration - 1;

  return taskStartDay <= durationEndDay && taskEndDay >= startDay;
};
const filterOverlappingTasks = (
  tasks: ProjectTemplateTask[],
  durationParam: DurationParam,
) => tasks.filter((x) => isOverlappingTask(x, durationParam));

interface Props {
  schedulerRef: SchedulerReference;
  relativeDate: Moment;
  isHoursPerWeek: boolean;
}

interface Result {
  getSourceTasksForAllocation: (
    allocation: TemplateAllocation,
  ) => Promise<ProjectTemplateTask[]>;
  allocationForShowingSourceTasks?: TemplateAllocation;
  resetAllocationForShowingSourceTasks: () => void;
}

export const useAutoAllocateSourceTasks = ({
  schedulerRef,
  relativeDate,
  isHoursPerWeek,
}: Props): Result => {
  const allTasks = useGetAllTasks();
  const tasks = useMemo(() => Object.values(allTasks), [allTasks]);

  const sourceTasksForAllocation = useCallback(
    (allocation: TemplateAllocation) => {
      const filteredTasks =
        allocation.type === ResourceType.User
          ? filterTasksByUserId(tasks, allocation.userId)
          : filterTasksByTeamId(tasks, allocation.teamId);
      const filteredTasksWithEffort = filterTasksWithEffort(filteredTasks);

      return filterOverlappingTasks(filteredTasksWithEffort, {
        startDay: allocation.startDay,
        duration: allocation.duration,
      });
    },
    [tasks],
  );
  const getSourceTasksForAllocation = useCallback(
    (allocation: TemplateAllocation) =>
      new Promise<ProjectTemplateTask[]>((resolve) => {
        resolve(sourceTasksForAllocation(allocation));
      }),
    [sourceTasksForAllocation],
  );
  const [showSourceTasksForAllocation, setAllocationForShowingSourceTasks] =
    useState<TemplateAllocation>();

  useShowSourceTasks(schedulerRef, ({ event }) =>
    setAllocationForShowingSourceTasks(
      asTemplateAllocation(
        relativeDate,
        isHoursPerWeek,
      )(asAllocationPayload(event)),
    ),
  );

  const resetAllocationForShowingSourceTasks = useCallback(() => {
    setAllocationForShowingSourceTasks(undefined);
  }, []);

  return {
    getSourceTasksForAllocation,
    allocationForShowingSourceTasks: showSourceTasksForAllocation,
    resetAllocationForShowingSourceTasks,
  };
};
